import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import {
  Button,
  Card,
  FormGroup,
  InputGroup,
  ProgressBar,
  Intent,
  Callout,
  HTMLSelect,
  Checkbox
} from '@blueprintjs/core'
import * as Sentry from '@sentry/browser'
import Client from '../../client'
import { toaster } from '../../Toast'
import CategoryPicker from '../../components/Categories/CategoryPicker'
import Uploader from '../../components/images/Uploader'
import { useSupplierSession } from './hooks/useSupplierSession'
import './style.scss'
import { blurActiveInput } from '../orders/packing/utils'
import PrinterToaster from '../../printing/toaster'
import PrinterV2 from '../../printing/printer'
import { CONDITION_OPTIONS, formatAmazonConditionForDescription } from '../../constants/amazon/ConditionOptions'

const CONDITION_DESCRIPTION_OPTIONS = [
  { label: 'Folds', value: 'Folds' },
  { label: 'Highlighter', value: 'Highlighter' },
  { label: 'Loose binding or pages', value: 'Loose binding or pages' },
  { label: 'Library owned', value: 'Library owned' },
  { label: 'Marks', value: 'Marks' },
  { label: 'Rips or scratches', value: 'Rips or scratches' },
  { label: 'Water damage', value: 'Water damage' }
]

const uploadImagesToS3 = async (
  images: Array<{ url?: string; key?: string; rank: number; rotation: number; file?: File }>
): Promise<Array<{ key: string; rank: number; rotation: number }>> => {
  const uploadedImages = []

  for (const image of images) {
    try {
      if (image.key && !image.key.startsWith('price-service-') && !image.key.startsWith('temp-')) {
        uploadedImages.push({
          stock: false,
          key: image.key,
          rank: image.rank,
          rotation: image.rotation
        })
      } else if (image.file) {
        const presignRes = await Client.presignV5(image.file.type)
        await Client.uploadS3V2(presignRes.data.url, presignRes.data.fields, image.file.type, image.file)

        uploadedImages.push({
          stock: false,
          key: presignRes.data.key,
          rank: image.rank,
          rotation: image.rotation
        })
      } else if (image.url && image.url.startsWith('data:')) {
        const response = await fetch(image.url)
        const blob = await response.blob()

        const presignRes = await Client.presignV5(blob.type || 'image/jpeg')
        await Client.uploadS3V2(presignRes.data.url, presignRes.data.fields, blob.type || 'image/jpeg', blob)

        uploadedImages.push({
          key: presignRes.data.key,
          rank: image.rank,
          rotation: image.rotation
        })
      } else if (image.url && (image.url.startsWith('http://') || image.url.startsWith('https://'))) {
        try {
          const response = await fetch(image.url)
          const blob = await response.blob()

          const presignRes = await Client.presignV5(blob.type || 'image/jpeg')
          await Client.uploadS3V2(presignRes.data.url, presignRes.data.fields, blob.type || 'image/jpeg', blob)

          uploadedImages.push({
            stock: true,
            key: presignRes.data.key,
            rank: image.rank,
            rotation: image.rotation
          })
        } catch (fetchError) {
          Sentry.captureException(fetchError)
        }
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  return uploadedImages
}

interface BookData {
  title?: string
  author?: string
  isbn?: string
  language?: string
  publisher?: string
  category?: string
  imageUrl?: string
  asin?: string
  listingPrice?: number
  shippingPrice?: number
  packageWeight?: number
  salesRank?: number
  length?: number
  width?: number
  height?: number
  weight?: number
  highestSalesRankCategory?: string
  classifications?: {
    classificationId: string
    displayName: string
  }
}

interface LocationState {
  bookData?: BookData
  isbn?: string
  asin?: string
}

interface UploaderImage {
  id?: number
  key: string
  url: string
  rotation: number
  rank?: number
  isEdited?: boolean
  oldKey?: string
  oldUrl?: string
  loading?: boolean
  failed?: boolean
  progress?: number
  removed?: boolean
  file?: File
}

export const NewListingPage: React.FC = () => {
  const location = useLocation<LocationState>()
  const history = useHistory()
  const initialBookData = location.state?.bookData
  const initialIsbn = location.state?.bookData?.isbn
  const initialAsin = location.state?.bookData?.asin

  const [sessionSupplier] = useSupplierSession()

  const [bookData, setBookData] = useState<BookData | null>(initialBookData || null)
  const [selectedCategory, setSelectedCategory] = useState<string | number>('')

  const [title, setTitle] = useState(initialBookData?.title || '')
  const [author, setAuthor] = useState(initialBookData?.author || '')
  const [isbn, setIsbn] = useState(initialBookData?.isbn || '')
  const [language, setLanguage] = useState(initialBookData?.language || '')
  const [publisher, setPublisher] = useState(initialBookData?.publisher || '')
  const [classifications, setClassifications] = useState<{ classificationId: string; displayName: string }>(
    initialBookData?.classifications || { classificationId: '', displayName: '' }
  )

  const [weight, setWeight] = useState(initialBookData?.weight)
  const [dimensionL, setDimensionL] = useState(initialBookData?.length)
  const [dimensionW, setDimensionW] = useState(initialBookData?.width)
  const [dimensionH, setDimensionH] = useState(initialBookData?.height)
  const [loading, setLoading] = useState(false)
  const [_sortDecision, setSortDecision] = useState<string>('')

  const [condition, setCondition] = useState('')
  const [conditionDescription, setConditionDescription] = useState<string[]>([])
  const [showDescription, setShowDescription] = useState(false)

  const [images, setImages] = useState<UploaderImage[]>([])

  const [submitting, setSubmitting] = useState(false)
  const [submitErrors, setSubmitErrors] = useState<any>(null)
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>('')

  const [userRemovedDefaultImage, setUserRemovedDefaultImage] = useState(false)
  const [hasAttemptedAutoCategory, setHasAttemptedAutoCategory] = useState(false)

  useEffect(() => {
    if (bookData?.imageUrl && !userRemovedDefaultImage) {
      const hasDefaultImage = images.some(img => img.url === bookData.imageUrl && !img.removed)

      if (!hasDefaultImage && images.filter(img => !img.removed).length === 0) {
        setImages([
          {
            key: `price-service-default-${Date.now()}`,
            url: bookData.imageUrl,
            rank: 0,
            rotation: 0
          }
        ])
      }
    }
  }, [bookData?.imageUrl, images, userRemovedDefaultImage])

  const onImageChange = (newImages: UploaderImage[], cb: Function = () => {}) => {
    const filteredImages = newImages.filter(img => !img.removed)

    if (bookData?.imageUrl) {
      const hadDefaultImage = images.some(img => img.url === bookData.imageUrl && !img.removed)
      const hasDefaultImage = filteredImages.some(img => img.url === bookData.imageUrl)

      if (hadDefaultImage && !hasDefaultImage) {
        setUserRemovedDefaultImage(true)
      }
    }

    setImages(filteredImages)
    cb(filteredImages)
  }

  const fetchBookData = useCallback(async (bookIsbn: string, asin: string) => {
    setLoading(true)
    try {
      const response = await Client.searchAmazonBooks({ query: bookIsbn, asin_query: asin })
      const books = response.data.books || []

      if (books.length > 0) {
        const book = books[0]
        setBookData({
          title: book.title,
          author: book.author,
          publisher: book.publisher,
          isbn: book.isbn,
          imageUrl: book.mainImageUrl,
          asin: book.asin,
          listingPrice: book.listingPrice,
          shippingPrice: book.shippingPrice,
          packageWeight: book.packageWeight,
          salesRank: book.salesRank,
          length: book.packageLength,
          width: book.packageWidth,
          height: book.packageHeight,
          highestSalesRankCategory: book.highestSalesRankCategory,
          classifications: book.classifications || { classificationId: '', displayName: '' }
        })

        setTitle(book.title || '')
        setAuthor(book.author || '')
        setIsbn(book.isbn || '')
        setLanguage(book.language || '')
        setPublisher(book.publisher || '')

        if (book.classifications && book.classifications.classificationId) {
          setClassifications(book.classifications)
        }

        if (book.packageWeight) {
          setWeight(book.packageWeight)
        }
        if (book.length || book.dimensions?.length) {
          setDimensionL(book.length || book.dimensions.length)
        }
        if (book.width || book.dimensions?.width) {
          setDimensionW(book.width || book.dimensions.width)
        }
        if (book.height || book.dimensions?.height) {
          setDimensionH(book.height || book.dimensions.height)
        }

        setSortDecision(response.data.sortDecision || '')
      } else {
        toaster.show({
          message: 'No book found for the provided ISBN',
          intent: Intent.WARNING,
          timeout: 3000
        })
      }
    } catch (err) {
      Sentry.captureException(err)

      let errorMessage = 'An unexpected error occurred while fetching book information.'
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.message) {
        errorMessage = err.message
      }

      toaster.show({
        message: errorMessage,
        intent: Intent.DANGER,
        timeout: 5000
      })
    } finally {
      setLoading(false)
    }
  }, [])

  const getCurrentCondition = useCallback(() => condition, [condition])
  const getCurrentConditionDescription = useCallback(() => conditionDescription, [conditionDescription])

  useEffect(() => {
    fetchBookData(initialIsbn, initialAsin)
  }, [initialIsbn, initialBookData, initialAsin, fetchBookData])

  // Automatically set category based on highestSalesRankCategory
  useEffect(() => {
    const autoSelectCategory = async () => {
      if (bookData?.highestSalesRankCategory && !selectedCategory && !hasAttemptedAutoCategory) {
        setHasAttemptedAutoCategory(true)
        try {
          const response = await Client.getCategoryMapping(bookData.highestSalesRankCategory)
          if (response.data?.category?.id) {
            setSelectedCategory(response.data.category.id)
          }
        } catch (error) {
          Sentry.captureException(error)
        }
      }
    }

    autoSelectCategory()
  }, [bookData?.highestSalesRankCategory, selectedCategory, hasAttemptedAutoCategory])

  const handleCategoryChange = (category: string | number) => {
    setSelectedCategory(category)
  }

  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({})

  const validateRequiredFields = useCallback(() => {
    const errors: Record<string, boolean> = {}
    if (!title.trim()) errors.title = true
    if (!author.trim()) errors.author = true
    if (!publisher.trim()) errors.publisher = true
    if (!isbn.trim()) errors.isbn = true
    if (!selectedCategory) errors.category = true
    if (!condition) errors.condition = true
    return errors
  }, [title, author, publisher, isbn, selectedCategory, condition])

  const compileTemplateDescription = useCallback(() => {
    let description = `${title} by ${author}. ISBN: ${isbn}. Published by ${publisher || 'Unknown'}.`

    if (condition) {
      description += ` Condition: ${formatAmazonConditionForDescription(condition)}.`
    }

    if (conditionDescription && conditionDescription.length > 0) {
      description += ` Condition issues: ${conditionDescription.join(', ')}.`
    }

    return description
  }, [title, author, isbn, publisher, condition, conditionDescription])

  const prepareImagesForUpload = useCallback(() => {
    return images.map((image, index) => ({
      ...image,
      rank: index,
      rotation: image.rotation || 0
    }))
  }, [images])

  const handleListAndPrint = async (event?: React.MouseEvent<HTMLElement>) => {
    const newTabPdfPrinter = event?.ctrlKey || event?.metaKey || false
    setSubmitErrors(null)
    setSubmitErrorMessage('')
    setSubmitting(true)

    try {
      const missingFields = validateRequiredFields()
      if (Object.keys(missingFields).length > 0) {
        setFieldErrors(missingFields)
        setSubmitErrorMessage(
          `Please fill out the following required fields: ${Object.keys(missingFields)
            .map(f => f.charAt(0).toUpperCase() + f.slice(1))
            .join(', ')}`
        )
        return
      }
      setFieldErrors({})

      if (!bookData?.listingPrice) {
        Sentry.captureMessage('Missing market price for book listing', { extra: { isbn, asin: bookData?.asin } })
        setSubmitErrorMessage('We couldn’t determine a market price right now. Please try again shortly.')
        setSubmitting(false)
        return
      }

      const finalDescription = compileTemplateDescription()
      let imagesForUpload = prepareImagesForUpload()

      if (imagesForUpload.length === 0 && bookData?.imageUrl) {
        imagesForUpload = [
          {
            key: `price-service-fallback-${Date.now()}`,
            url: bookData.imageUrl,
            rank: 0,
            rotation: 0
          }
        ]
      }

      const imagesAttributes = await uploadImagesToS3(imagesForUpload)

      const listingPrice = bookData?.listingPrice
      const shippingPrice = bookData?.shippingPrice || 0
      const landedPrice = listingPrice + shippingPrice
      const bookRequest = {
        isbn: isbn.trim(),
        title: title.trim(),
        author: author.trim(),
        language: language.trim() || 'English',
        publisher: publisher.trim(),
        description: finalDescription,
        price: landedPrice,
        condition,
        asin: bookData?.asin,
        category_id: typeof selectedCategory === 'string' ? parseInt(selectedCategory) : selectedCategory,
        inventory_location_id: null,
        supplier_id: sessionSupplier,
        images_attributes: imagesAttributes,
        classifications,
        ...(weight && { weight }),
        ...(dimensionL && { length: dimensionL }),
        ...(dimensionW && { width: dimensionW }),
        ...(dimensionH && { height: dimensionH })
      }

      const response = await Client.createBook(bookRequest)

      if (!response.data?.product?.id) {
        throw new Error('Book creation failed - no product ID returned')
      }

      const createdProduct = response.data.product
      const _productId = createdProduct.id
      const productSku = createdProduct.sku
      const wasRestocked = response.data.restocked
      const inventoryUnitId = createdProduct.inventoryUnits[createdProduct.inventoryUnits.length - 1].id

      const actionText = wasRestocked ? 'restocked' : 'created'
      toaster.show({
        message: `Book "${title}" was ${actionText} successfully! (SKU: ${productSku})`,
        intent: Intent.SUCCESS,
        timeout: 5000
      })

      const printer = new PrinterV2(newTabPdfPrinter)

      try {
        const success = await printer.printMediaTicketFromId(inventoryUnitId)
        if (!success) {
          PrinterToaster.show({
            intent: 'danger',
            message: 'Unable to print media ticket'
          })
        }
      } catch (printError) {
        Sentry.captureException(printError)
        PrinterToaster.show({
          intent: 'danger',
          message: 'An unexpected error occurred while printing the media ticket'
        })
      } finally {
        blurActiveInput()
      }

      history.push('/scan-to-list-books')
    } catch (error) {
      Sentry.captureException(error)

      if (error?.response?.data?.errors) {
        setSubmitErrors(error.response.data.errors)

        const errorMessages = Object.entries(error.response.data.errors)
          .map(([field, messages]: [string, string[]]) => {
            const fieldName = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
            return `${fieldName}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
          })
          .join('; ')

        setSubmitErrorMessage(`Please fix these errors: ${errorMessages}`)
      } else if (error?.response?.data?.error) {
        setSubmitErrorMessage(error.response.data.error)
      } else if (error?.response?.status === 422) {
        setSubmitErrorMessage('The book data is invalid. Please check all fields and try again.')
      } else if (error?.response?.status >= 500) {
        setSubmitErrorMessage('Server error occurred. Please try again later.')
      } else {
        setSubmitErrorMessage(error?.message || 'An unexpected error occurred while creating the book.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const renderConditionForm = () => {
    return (
      <div className="condition-form">
        <FormGroup label="Condition" labelInfo="(required)">
          <HTMLSelect
            value={condition}
            onChange={e => setCondition(e.target.value)}
            options={[{ label: 'Select condition...', value: '' }, ...CONDITION_OPTIONS]}
            fill
          />
        </FormGroup>

        <FormGroup label="Condition Details" labelInfo="(optional)">
          <div className="condition-checkboxes">
            {CONDITION_DESCRIPTION_OPTIONS.map(option => (
              <Checkbox
                key={option.value}
                label={option.label}
                checked={conditionDescription.includes(option.value)}
                onChange={e => {
                  const isChecked = (e.target as HTMLInputElement).checked
                  if (isChecked) {
                    setConditionDescription([...conditionDescription, option.value])
                  } else {
                    setConditionDescription(conditionDescription.filter(item => item !== option.value))
                  }
                }}
              />
            ))}
          </div>
        </FormGroup>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="new-listing-page loading-state">
        <div className="loading-content">
          <ProgressBar />
          <p>Loading book information...</p>
        </div>
      </div>
    )
  }

  const renderTemplateContent = () => {
    return renderConditionForm()
  }

  return (
    <div className="new-listing-page">
      <div className="listing-header">
        <div className="progress-section">
          <h1>New Listing</h1>
        </div>
        <Button
          intent="primary"
          large
          className="list-print-button"
          onClick={e => handleListAndPrint(e)}
          loading={submitting}
          disabled={submitting}
        >
          {submitting ? 'Creating Product...' : 'List and print'}
        </Button>
      </div>

      {submitErrorMessage && (
        <Callout intent={Intent.DANGER} title="Error Creating Product" className="error-section">
          <p>{submitErrorMessage}</p>
          {submitErrors && (
            <div className="error-details">
              <details>
                <summary className="error-summary">View detailed errors</summary>
                <pre className="error-pre">{JSON.stringify(submitErrors, null, 2)}</pre>
              </details>
            </div>
          )}
        </Callout>
      )}

      <div className="listing-content">
        <div className="listing-main-row">
          <div className="product-column">
            <Card className="product-section">
              <h3 className="section-title">PRODUCT</h3>

              <FormGroup
                label="Title"
                intent={fieldErrors.title ? Intent.DANGER : undefined}
                helperText={fieldErrors.title ? 'Title is required' : undefined}
              >
                <InputGroup
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className="product-input"
                  intent={fieldErrors.title ? Intent.DANGER : undefined}
                />
              </FormGroup>

              <FormGroup
                label="Category"
                intent={fieldErrors.category ? Intent.DANGER : undefined}
                helperText={fieldErrors.category ? 'Category is required' : undefined}
              >
                <CategoryPicker value={selectedCategory} onChange={handleCategoryChange} width="100%" />
              </FormGroup>

              <FormGroup
                label="Author"
                intent={fieldErrors.author ? Intent.DANGER : undefined}
                helperText={fieldErrors.author ? 'Author is required' : undefined}
              >
                <InputGroup
                  value={author}
                  onChange={e => setAuthor(e.target.value)}
                  className="product-input"
                  intent={fieldErrors.author ? Intent.DANGER : undefined}
                />
              </FormGroup>

              <FormGroup
                label="ISBN"
                intent={fieldErrors.isbn ? Intent.DANGER : undefined}
                helperText={fieldErrors.isbn ? 'ISBN is required' : undefined}
              >
                <InputGroup
                  value={isbn}
                  onChange={e => setIsbn(e.target.value)}
                  className="product-input"
                  intent={fieldErrors.isbn ? Intent.DANGER : undefined}
                />
              </FormGroup>

              <FormGroup label="Language">
                <InputGroup
                  value={language || 'English'}
                  onChange={e => setLanguage(e.target.value)}
                  className="product-input"
                />
              </FormGroup>

              <FormGroup
                label="Publisher"
                intent={fieldErrors.publisher ? Intent.DANGER : undefined}
                helperText={fieldErrors.publisher ? 'Publisher is required' : undefined}
              >
                <InputGroup
                  value={publisher}
                  onChange={e => setPublisher(e.target.value)}
                  className="product-input"
                  intent={fieldErrors.publisher ? Intent.DANGER : undefined}
                />
              </FormGroup>
            </Card>

            <Card className="measurements-section">
              <h3 className="section-title">WEIGHT AND MEASUREMENTS</h3>

              <div className="measurements-content">
                <div className="weight-section">
                  <FormGroup label="Item Weight">
                    <div className="weight-input-container">
                      <InputGroup
                        value={weight?.toString() || ''}
                        onChange={e => setWeight(parseFloat(e.target.value) || 0)}
                        className="weight-input"
                      />
                      <span className="weight-unit">LBS</span>
                    </div>
                  </FormGroup>
                </div>

                <div className="dimensions-section">
                  <FormGroup label="Dimensions">
                    <div className="dimensions-inputs">
                      <div className="dimension-group">
                        <div>
                          <InputGroup
                            value={dimensionL?.toString() || ''}
                            onChange={e => setDimensionL(parseFloat(e.target.value) || 0)}
                            className="dimension-input"
                          />
                          <span className="dimension-unit">IN</span>
                        </div>

                        <span className="dimension-label">L</span>
                      </div>
                      <div className="dimension-group">
                        <InputGroup
                          value={dimensionW?.toString() || ''}
                          onChange={e => setDimensionW(parseFloat(e.target.value) || 0)}
                          className="dimension-input"
                        />
                        <span className="dimension-unit">IN</span>
                        <span className="dimension-label">W</span>
                      </div>
                      <div className="dimension-group">
                        <InputGroup
                          value={dimensionH?.toString() || ''}
                          onChange={e => setDimensionH(parseFloat(e.target.value) || 0)}
                          className="dimension-input"
                        />
                        <span className="dimension-unit">IN</span>
                        <span className="dimension-label">H</span>
                      </div>
                    </div>
                  </FormGroup>
                </div>
              </div>
            </Card>
          </div>

          <Card className="attributes-section">
            <h3 className="section-title">
              {showDescription ? 'DESCRIPTION' : 'PRODUCT ATTRIBUTES'}
              <Button
                minimal
                small
                className="view-description-toggle"
                onClick={() => setShowDescription(!showDescription)}
              >
                {showDescription ? 'Show Attributes' : 'View Description'}
              </Button>
            </h3>

            {showDescription ? (
              <div className="product-details">
                <div className="product-info-grid">
                  <div className="info-row">
                    <strong className="info-label">Title:</strong>
                    <span className="info-value">{title || ''}</span>
                  </div>
                  <div className="info-row">
                    <strong className="info-label">Author:</strong>
                    <span className="info-value">{author || 'Robin Wall Kimmerer'}</span>
                  </div>

                  {getCurrentCondition() && (
                    <div className="info-row">
                      <strong className="info-label">Condition:</strong>
                      <span className="info-value info-value--condition">{getCurrentCondition()}</span>
                    </div>
                  )}

                  {getCurrentConditionDescription() && getCurrentConditionDescription().length > 0 && (
                    <div className="info-row">
                      <strong className="info-label">Condition Details:</strong>
                      <span className="info-value">{getCurrentConditionDescription().join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              renderTemplateContent()
            )}
          </Card>

          <Card className="images-section">
            <h3 className="section-title">IMAGES</h3>

            <div className="uploader-container">
              <Uploader images={images} onChange={onImageChange} maxImages={10} disableUpload={false} />
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default NewListingPage
